import { __awaiter, __extends, __generator } from "tslib";
import { invalidUrlErrorMessage, SdkMessageTypes } from '../constants';
import { ErrorCode } from '../hosted-web-content-sdk.interface';
import { isValidUrlForApp } from '../utils';
import { getPageDisplayNameFromAppDefinition } from '../utils/helpers';
import { BaseMessageHandler } from './base-message-handler';
import { callServiceAndFormulateLogMessageResult, createSdkError, formulateAppMessageResult, formulateSdkResponse, logMessageResult, onSdkError, onStatusError, } from './messageHandlingUtils';
/**
 * Responsible for pages module related message requests.
 */
var PageMessageHandler = /** @class */ (function (_super) {
    __extends(PageMessageHandler, _super);
    function PageMessageHandler(appSdkHost, pageService, telemetryService) {
        var _this = _super.call(this, appSdkHost, telemetryService) || this;
        _this.appSdkHost = appSdkHost;
        _this.pageService = pageService;
        _this.telemetryService = telemetryService;
        _this.getConfig = function () { return __awaiter(_this, void 0, void 0, function () {
            var result, _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        if (!(this.pageService && this.pageService.getConfig)) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.pageService.getConfig()];
                    case 1:
                        _a = _b.sent();
                        return [3 /*break*/, 3];
                    case 2:
                        _a = this.getDefaultConfig();
                        _b.label = 3;
                    case 3:
                        result = _a;
                        return [2 /*return*/, formulateAppMessageResult(result)];
                }
            });
        }); };
        return _this;
    }
    PageMessageHandler.prototype.initHandlers = function () {
        var _this = this;
        this.registerHandler(SdkMessageTypes.pages.getConfig, this.getConfig);
        this.registerHandler(SdkMessageTypes.pages.returnFocus, function (_, params) {
            return callServiceAndFormulateLogMessageResult(_this.pageService.returnFocus, params);
        });
        this.registerHandler(SdkMessageTypes.pages.setCurrentFrame, this.handleSetCurrentFrame);
        this.registerHandler(SdkMessageTypes.pages.navigateCrossDomain, this.handleLoadCrossDomainUrl, {
            errorCallback: function (error, scenario) { return onStatusError(SdkMessageTypes.pages.navigateCrossDomain, error, scenario); },
        });
        this.registerHandler(SdkMessageTypes.pages.navigateToApp, function (_, params) { return __awaiter(_this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.pageService.navigateToApp(params)];
                    case 1:
                        _a.sent();
                        return [2 /*return*/, formulateAppMessageResult(true)];
                }
            });
        }); }, {
            errorCallback: function (error, scenario) { return onStatusError(SdkMessageTypes.pages.navigateToApp, error, scenario); },
        });
        this.registerHandler(SdkMessageTypes.pages.shareDeepLink, function (_, subEntityId, subEntityLabel, subEntityWebUrl) {
            return callServiceAndFormulateLogMessageResult(_this.pageService.shareDeepLink, {
                subEntityId: subEntityId,
                subEntityLabel: subEntityLabel,
                subEntityWebUrl: subEntityWebUrl,
            });
        });
        this.registerHandler(SdkMessageTypes.pages.currentApp.navigateTo, this.buildParamsAndCallNavigateToApp, {
            errorCallback: function (error, scenario) { return onSdkError(SdkMessageTypes.pages.currentApp.navigateTo, error, scenario); },
        });
        this.registerHandler(SdkMessageTypes.pages.currentApp.navigateToDefaultPage, this.buildParamsAndCallNavigateToApp, {
            errorCallback: function (error, scenario) {
                return onSdkError(SdkMessageTypes.pages.currentApp.navigateToDefaultPage, error, scenario);
            },
        });
    };
    PageMessageHandler.prototype.validateDependencies = function (message) {
        if (!this.pageService && message !== SdkMessageTypes.pages.getConfig) {
            throw 'No pageService provided';
        }
    };
    PageMessageHandler.prototype.getDefaultConfig = function () {
        var origin = this.appSdkHost.getOrigin();
        if (!origin) {
            throw new Error('Origin not set of AppSdkHost');
        }
        var appContext = this.appSdkHost.getContext();
        var appDefinition = this.appSdkHost.getAppDefinition();
        var suggestedDisplayName = appDefinition && getPageDisplayNameFromAppDefinition(appDefinition, appContext.page.id);
        return {
            contentUrl: origin,
            entityId: appContext.page.id,
            suggestedDisplayName: suggestedDisplayName,
        };
    };
    PageMessageHandler.prototype.handleSetCurrentFrame = function (_, frameInfo) {
        return __awaiter(this, void 0, void 0, function () {
            var context;
            return __generator(this, function (_a) {
                context = this.appSdkHost.getContext();
                if (isValidUrlForApp(frameInfo.contentUrl, this.appSdkHost.getLogger(), this.appDefinition, context) &&
                    isValidUrlForApp(frameInfo.websiteUrl, this.appSdkHost.getLogger(), this.appDefinition, context)) {
                    this.pageService.setCurrentFrame(frameInfo);
                }
                return [2 /*return*/, logMessageResult];
            });
        });
    };
    /**
     * Load cross-domain url in frame
     */
    PageMessageHandler.prototype.handleLoadCrossDomainUrl = function (_, url) {
        return __awaiter(this, void 0, void 0, function () {
            var _a, _b, _c, _d, _e;
            return __generator(this, function (_f) {
                switch (_f.label) {
                    case 0:
                        _a = isValidUrlForApp;
                        _b = [url, this.appSdkHost.getLogger(), this.appDefinition];
                        return [4 /*yield*/, this.appSdkHost.getAppContext()];
                    case 1:
                        if (!_a.apply(void 0, _b.concat([_f.sent()]))) return [3 /*break*/, 4];
                        _d = (_c = this.appSdkHost).load;
                        _e = [url];
                        return [4 /*yield*/, this.appSdkHost.getAppContext()];
                    case 2: return [4 /*yield*/, _d.apply(_c, _e.concat([_f.sent()]))];
                    case 3:
                        _f.sent();
                        return [2 /*return*/, formulateAppMessageResult(true)];
                    case 4: throw invalidUrlErrorMessage;
                }
            });
        });
    };
    PageMessageHandler.prototype.buildParamsAndCallNavigateToApp = function (_, params) {
        return __awaiter(this, void 0, void 0, function () {
            var navigateToAppParams, appDefinition, result;
            return __generator(this, function (_a) {
                appDefinition = this.appSdkHost.getAppDefinition();
                if (!(appDefinition === null || appDefinition === void 0 ? void 0 : appDefinition.id)) {
                    throw createSdkError(ErrorCode.INTERNAL_ERROR, 'An error ocured while locating application ID');
                }
                if (!params) {
                    navigateToAppParams = {
                        appId: appDefinition === null || appDefinition === void 0 ? void 0 : appDefinition.id,
                        pageId: (appDefinition === null || appDefinition === void 0 ? void 0 : appDefinition.pages.static[0].entityId) ? appDefinition.pages.static[0].entityId : '',
                    };
                }
                else {
                    navigateToAppParams = {
                        appId: appDefinition === null || appDefinition === void 0 ? void 0 : appDefinition.id,
                        pageId: params.pageId,
                        subPageId: params.subPageId,
                    };
                }
                try {
                    result = formulateSdkResponse(navigateToAppParams, this.pageService.navigateToApp);
                }
                catch (e) {
                    throw createSdkError(ErrorCode.INTERNAL_ERROR, e);
                }
                return [2 /*return*/, result];
            });
        });
    };
    return PageMessageHandler;
}(BaseMessageHandler));
export { PageMessageHandler };
//# sourceMappingURL=page-message-handler.js.map